/**
 * Shared widget position clamping utility.
 *
 * Problems this solves vs. the old per-widget hardcoded approach:
 *
 * 1. Hardcoded SAFE_MARGIN.left (88px) assumed the sidebar was always present and
 *    80px wide.  On tablet (viewport < 991px) the sidebar is off-canvas, so the
 *    first 88px of the screen was incorrectly off-limits.  We now read the
 *    sidebar's actual rendered right edge from the DOM and only reserve that
 *    space when the sidebar is actually on-screen.
 *
 * 2. The app uses CSS transform: scale(--app-scale) with transform-origin: top
 *    left.  Because the .app-scale ancestor has a CSS transform, position:fixed
 *    children are positioned relative to that ancestor (not the viewport), so
 *    the usable coordinate space is window.innerWidth / scale, not
 *    window.innerWidth.  Using the raw window dimensions caused widgets to hit
 *    an invisible right/bottom wall well before the visual edge of the screen on
 *    tablet and laptop.
 *
 * 3. window.innerHeight fluctuates on iOS Safari as the browser toolbar
 *    appears/disappears.  We prefer window.visualViewport which reflects only
 *    the stable visible area.
 *
 * 4. The old resize useEffect only listened to the window resize event.  Safari
 *    fires visualViewport resize (not window resize) when its toolbar toggles,
 *    so the clamp never re-ran after a toolbar change.  addViewportResizeListener
 *    and removeViewportResizeListener handle both events.
 */

// ─── helpers ──────────────────────────────────────────────────────────────────

export function getAppScale() {
  if (typeof window === "undefined") return 1;
  const el = document.querySelector(".app-scale");
  if (el) {
    const raw = getComputedStyle(el).getPropertyValue("--app-scale");
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return 1;
}

const clamp = (v, min, max) => Math.max(min, Math.min(v, max));

export function getAppScaledPointerClientPoint(event) {
  const touch = event?.touches?.[0] || event?.changedTouches?.[0];
  const clientX = touch?.clientX ?? event?.clientX ?? 0;
  const clientY = touch?.clientY ?? event?.clientY ?? 0;
  const scale = getAppScale();

  return {
    clientX: clientX / scale,
    clientY: clientY / scale,
  };
}

export const DEFAULT_DRAGGABLE_WIDGET_CHROME_TOP_OFFSET = 34;

const toFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeTopOffset = (topOffset) => Math.max(0, toFiniteNumber(topOffset, 0));

export function isPointerReleasedDuringDrag(event) {
  return Boolean(event && "buttons" in event && event.buttons === 0 && !event.touches?.length);
}

const readPresentViewportMetrics = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const scale = getAppScale();
  const vpWVisual = window.visualViewport?.width ?? window.innerWidth;
  const vpHVisual = window.visualViewport?.height ?? window.innerHeight;
  const vw = vpWVisual / scale;
  const vh = vpHVisual / scale;

  let minX = 8;
  const sidebar = document.querySelector(".sidebar, .automation-preview-sidebar");
  if (sidebar) {
    const rect = sidebar.getBoundingClientRect();
    if (rect.right > 0) minX = rect.right / scale + 8;
  }

  let bottomReserved = 8;
  const bottomNavEls = document.querySelectorAll(
    ".present-navbar-launch-section, .present-navbar-tabs-section, .present-navbar-datepicker-section"
  );
  bottomNavEls.forEach((el) => {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || parseFloat(style.opacity) < 0.01) return;
    const rect = el.getBoundingClientRect();
    if (rect.top >= vpHVisual) return;
    const fromBottom = (vpHVisual - rect.top) / scale + 8;
    if (fromBottom > bottomReserved) bottomReserved = fromBottom;
  });

  // Top and right can use the true viewport edge. Left and bottom keep their
  // own offsets for the sidebar and bottom Present navigation.
  const minY = 0;
  const edgePadding = 0;

  return {
    scale,
    vw,
    vh,
    minX,
    minY,
    bottomReserved,
    edgePadding,
  };
};

/**
 * Widget-independent Present safe rectangle (top-left placement region).
 * Width/height describe the span available for positioning and scaling overlays.
 */
export function getPresentSafeArea() {
  const metrics = readPresentViewportMetrics();
  if (!metrics) {
    return null;
  }

  const { vw, vh, minX, minY, bottomReserved, edgePadding } = metrics;

  return {
    left: minX,
    top: minY,
    width: Math.max(1, vw - minX - edgePadding),
    height: Math.max(1, vh - minY - bottomReserved),
    viewportWidth: vw,
    viewportHeight: vh,
    bottomReserved,
    edgePadding,
  };
}

/**
 * Max top-left (x, y) for a widget of the given size within Present bounds.
 */
export function getPresentPlacementExtents(widgetWidth = 0, widgetHeight = 0, safeArea, { topOffset = 0 } = {}) {
  const resolvedTopOffset = normalizeTopOffset(topOffset);

  if (safeArea) {
    const width = Math.max(0, Number(widgetWidth) || 0);
    const height = Math.max(0, Number(widgetHeight) || 0);
    const minX = toFiniteNumber(safeArea.left, 8);
    const safeTop = toFiniteNumber(safeArea.top, 8);
    const minY = safeTop + resolvedTopOffset;
    const safeWidth = Math.max(1, toFiniteNumber(safeArea.width, 1));
    const safeHeight = Math.max(1, toFiniteNumber(safeArea.height, 1));

    return {
      minX,
      minY,
      maxX: Math.max(minX, minX + safeWidth - width),
      maxY: Math.max(minY, safeTop + safeHeight - height),
    };
  }

  const metrics = readPresentViewportMetrics();
  if (!metrics) {
    return null;
  }

  const { vw, vh, minX, minY, bottomReserved, edgePadding } = metrics;
  const width = Math.max(0, Number(widgetWidth) || 0);
  const height = Math.max(0, Number(widgetHeight) || 0);
  const adjustedMinY = minY + resolvedTopOffset;

  return {
    minX,
    minY: adjustedMinY,
    maxX: Math.max(minX, vw - width - edgePadding),
    maxY: Math.max(adjustedMinY, vh - height - bottomReserved),
  };
}

export function clampWidgetSize(
  width,
  height,
  { minWidth = 1, minHeight = 1, maxWidth, maxHeight, safeArea, topOffset = 0 } = {}
) {
  const resolvedSafeArea = safeArea === undefined ? getPresentSafeArea() : safeArea;
  const resolvedTopOffset = normalizeTopOffset(topOffset);
  const areaMaxWidth = maxWidth ?? resolvedSafeArea?.width;
  const areaMaxHeight = maxHeight ?? (
    resolvedSafeArea ? Math.max(1, resolvedSafeArea.height - resolvedTopOffset) : undefined
  );
  const resolvedMaxWidth = Number.isFinite(Number(areaMaxWidth))
    ? Math.max(1, Number(areaMaxWidth))
    : Infinity;
  const resolvedMaxHeight = Number.isFinite(Number(areaMaxHeight))
    ? Math.max(1, Number(areaMaxHeight))
    : Infinity;
  const resolvedMinWidth = Math.min(resolvedMaxWidth, Math.max(1, Number(minWidth) || 1));
  const resolvedMinHeight = Math.min(resolvedMaxHeight, Math.max(1, Number(minHeight) || 1));

  return {
    width: Math.round(clamp(toFiniteNumber(width, resolvedMinWidth), resolvedMinWidth, resolvedMaxWidth)),
    height: Math.round(clamp(toFiniteNumber(height, resolvedMinHeight), resolvedMinHeight, resolvedMaxHeight)),
  };
}

export function clampWidgetSizeAndPosition({
  position = {},
  size = {},
  minWidth = 1,
  minHeight = 1,
  maxWidth,
  maxHeight,
  safeArea,
  topOffset = 0,
} = {}) {
  const resolvedSafeArea = safeArea === undefined ? getPresentSafeArea() : safeArea;
  const resolvedTopOffset = normalizeTopOffset(topOffset);
  const clampedSize = clampWidgetSize(size.width, size.height, {
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    safeArea: resolvedSafeArea,
    topOffset: resolvedTopOffset,
  });
  const fallbackX = resolvedSafeArea?.left ?? 0;
  const fallbackY = resolvedSafeArea?.top ?? 0;
  const clampedPosition = clampWidgetPosition(
    toFiniteNumber(position.x, fallbackX),
    toFiniteNumber(position.y, fallbackY),
    clampedSize.width,
    clampedSize.height,
    resolvedSafeArea,
    { topOffset: resolvedTopOffset }
  );

  return {
    position: clampedPosition,
    size: clampedSize,
  };
}

export function clampWidgetLayoutToSafeArea({
  x,
  y,
  width,
  height,
  minWidth = 1,
  minHeight = 1,
  maxWidth,
  maxHeight,
  safeArea,
  topOffset = 0,
} = {}) {
  const { position, size } = clampWidgetSizeAndPosition({
    position: { x, y },
    size: { width, height },
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    safeArea,
    topOffset,
  });

  return {
    x: position.x,
    y: position.y,
    width: size.width,
    height: size.height,
  };
}

export function clampChromeWidgetSize(width, height, options = {}) {
  return clampWidgetSize(width, height, {
    ...options,
    topOffset: options.topOffset ?? DEFAULT_DRAGGABLE_WIDGET_CHROME_TOP_OFFSET,
  });
}

export function clampChromeWidgetSizeAndPosition(options = {}) {
  return clampWidgetSizeAndPosition({
    ...options,
    topOffset: options.topOffset ?? DEFAULT_DRAGGABLE_WIDGET_CHROME_TOP_OFFSET,
  });
}

// ─── main export ──────────────────────────────────────────────────────────────

/**
 * Clamps a widget's proposed (x, y) so it stays within the visible, usable
 * area of the screen, accounting for:
 *   - the CSS app-scale transform
 *   - the sidebar (left boundary, read from the DOM)
 *   - the bottom navigation bar (bottom boundary, read from the DOM)
 *   - visualViewport for stable iOS dimensions
 *
 * @param {number} x      proposed left position (widget coordinate space)
 * @param {number} y      proposed top  position (widget coordinate space)
 * @param {number} w      widget width
 * @param {number} h      widget height
 * @returns {{ x: number, y: number }}
 */
export function clampWidgetPosition(x, y, w, h, safeArea, options = {}) {
  const extents = getPresentPlacementExtents(w, h, safeArea, options);
  if (!extents) return { x, y };

  return {
    x: clamp(toFiniteNumber(x, extents.minX), extents.minX, extents.maxX),
    y: clamp(toFiniteNumber(y, extents.minY), extents.minY, extents.maxY),
  };
}

export function clampChromeWidgetPosition(x, y, w, h, safeArea) {
  return clampWidgetPosition(x, y, w, h, safeArea, {
    topOffset: DEFAULT_DRAGGABLE_WIDGET_CHROME_TOP_OFFSET,
  });
}

// ─── viewport resize helpers ──────────────────────────────────────────────────

/**
 * Registers a callback for both window resize and visualViewport resize.
 * Use this in place of window.addEventListener('resize', ...) so that the
 * clamp also re-runs when the iOS Safari toolbar appears / disappears.
 */
export function addViewportResizeListener(fn) {
  window.addEventListener("resize", fn);
  window.visualViewport?.addEventListener("resize", fn);
}

/**
 * Removes a callback that was registered with addViewportResizeListener.
 */
export function removeViewportResizeListener(fn) {
  window.removeEventListener("resize", fn);
  window.visualViewport?.removeEventListener("resize", fn);
}
